import {
  FUSIONNOW_BASE_URL,
  getFusionNowToken,
  FusionNowNotSubscribedError
} from './fusionNowAuth';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ActivityStatsData {
  emails_sent: number;
  sms_sent: number;
  ams_tasks: number;
}

export interface WorkflowSummaryData {
  total: number;
  running: number;
  stopped: number;
  draft: number;
}

export interface WorkflowTypeSummary {
  key: string;
  name: string;
  status: string;
  contacts_count: number;
  emails_sent: number;
  sms_sent: number;
}

export interface WorkflowTypeGroup {
  total: number;
  workflows: WorkflowTypeSummary[];
}

export interface WorkflowTypeStatsData {
  top_triggers: Array<{ trigger: string; workflows_count: number }>;
  insured_workflows: WorkflowTypeGroup;
  prospect_workflows: WorkflowTypeGroup;
  policy_workflows: WorkflowTypeGroup;
}

// ── Shared fetch helper ───────────────────────────────────────────────────────

async function analyticsGet<T>(path: string): Promise<T> {
  const token = await getFusionNowToken();
  const res = await fetch(`${FUSIONNOW_BASE_URL}/api/open_api/v1${path}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (res.status === 401 || res.status === 403) throw new FusionNowNotSubscribedError();
  if (!res.ok) throw new Error(`Analytics API error: ${res.status}`);

  const json = await res.json();
  if (!json.success) throw new Error(json.message ?? 'Analytics API returned success: false');

  return json.data as T;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export function getActivityStats(): Promise<ActivityStatsData> {
  return analyticsGet<ActivityStatsData>('/analytics/activity_stats');
}

export function getWorkflowSummary(): Promise<WorkflowSummaryData> {
  return analyticsGet<WorkflowSummaryData>('/analytics/workflow_summary');
}

export function getWorkflowTypeStats(): Promise<WorkflowTypeStatsData> {
  return analyticsGet<WorkflowTypeStatsData>('/analytics/workflow_type_stats');
}
